<script lang="ts">
    
    import { createEventDispatcher } from "svelte";
    import { Button, Col, FormCheck, FormGroup, Icon, Input, Label, Row, TabContent, TabPane } from "sveltestrap";
    import type { Room } from "../../Model/Room";
    import { picturesService, pictureUrlFactory, roomsFactory, roomsService } from "../../Services";
    import { State } from "../../Model/State";
    import ControlFeedback from "../ControlFeedback.svelte";
    import ControlLabel from "../ControlLabel.svelte";
    import FormFeedback from "../FormFeedback.svelte";
    import InvalidFeedback from "../InvalidFeedback.svelte";
    import PictureControl from "../Shared/PictureControl.svelte";
    import MessagesManager from "../Messages/MessagesManager.svelte";
    import InputFiles from "../Shared/InputFiles.svelte";
    import Each from "../Shared/Each.svelte";
    import AspectRatio from "../Shared/AspectRatio.svelte";
    import { RoomReward } from "../../Model/RoomReward";
    import { RoomRewards } from "../../Model/RoomRewards";
    import { showNotification } from "../../Services/ui";

    export let room: Room;

    const dispatch = createEventDispatcher();

    let formFeedback: FormFeedback;
    let isSaving = false;
    let name: string;
    let enabled: boolean;
    let showRoomQrInMonitor: boolean;
    let integratedDisplay: boolean;
    let secondsBetweenMessages: number = 10;
    let pictureId: State<string | null>;
    let inputFiles: InputFiles;
    let awardBingoText = '';
    let awardFigureText = '';

    $: setupRoom(room);

    function setupRoom(room: Room): void {
        name = room.name.value;
        enabled = room.enabled.value;
        integratedDisplay = room.integratedDisplay.value;
        showRoomQrInMonitor = room.showRoomQrInMonitor.value;
        secondsBetweenMessages = room.secondsBetweenMessages.value;
        awardBingoText = room.roomRewards.bingo.text.value;
        awardFigureText = room.roomRewards.figure.text.value;
        pictureId = new State(room.pictureId.value);
    }

    async function onSubmit(): Promise<void> {
        formFeedback.validate();

        if (isSaving || !formFeedback.isValid()) {
            return;
        }

        try {

            isSaving = true;

            const roomsRewards = new RoomRewards(
                new RoomReward(awardBingoText),
                new RoomReward(awardFigureText)
            );

            const clon = roomsFactory.createRoom(
                room.accountId,
                room.id,
                enabled,
                integratedDisplay,
                showRoomQrInMonitor,
                pictureId.value,
                name,
                secondsBetweenMessages,
                room.state.value,
                room.gallery.toJSON(),
                roomsRewards,
            );

            await roomsService.saveRoom(clon);

            room.enabled.set(enabled);
            room.integratedDisplay.set(integratedDisplay);
            room.showRoomQrInMonitor.set(showRoomQrInMonitor);
            room.pictureId.set(pictureId.value);
            room.name.set(name);
            room.secondsBetweenMessages.set(secondsBetweenMessages);
            room.roomRewards.bingo.text.set(awardBingoText);
            room.roomRewards.figure.text.set(awardFigureText);

            showNotification({
                type: 'success',
                content: 'La sala ha sido guardada'
            });

            dispatch('saved');

        } catch (error) {
            console.error(error);

            showNotification({
                type: 'fail',
                content: 'Ha ocurrido un error al guardar la sala'
            });

        } finally {
            isSaving = false;
        }
    }

    async function onPictures({ detail: [file] }: CustomEvent<File[]>): Promise<void> {
        try {
            const pictureId = await picturesService.uploadPicture(file, 1201);
            room.gallery.add(pictureId);
            await roomsService.saveRoom(room);
        } catch (error: any) {
            console.error(error);

            if (error.name === 'file_too_big') {
                showNotification({
                    type: 'fail',
                    content: error.message
                });
            }
        }
    }

    async function onRemovePicture(pictureId: string): Promise<void> {
        if (!confirm('¿Seguro que desea eliminar esta imagen?')) {
            return;
        }
        try {
            room.gallery.delete(pictureId);
            await roomsService.saveRoom(room);
            await picturesService.deletePicture(pictureId);
        } catch (error) {
            console.error(error);
        }
    }

</script>

<TabContent>

    <TabPane tab="Sala" tabId="form" class="pt-3" active>

        <FormFeedback bind:this={formFeedback} let:isInvalid>

            <Row>
                <Col xs={4}>
                    <FormGroup>
                        <Label>Imagen</Label>
                        <PictureControl id={pictureId} />
                    </FormGroup>
                </Col>
                <Col xs={8}>
                
                    <FormGroup>
                        <ControlFeedback let:invalid let:input let:valueMissing>
                            <ControlLabel required invalid={input && invalid}>Nombre</ControlLabel>
                            <input type="text" required class="form-control" bind:value={name}>
                            <InvalidFeedback show={input && valueMissing}>
                                Debe escibir un nombre de sala
                            </InvalidFeedback>
                        </ControlFeedback>
                    </FormGroup>
        
                    <FormGroup>
                        <Label>Modo de sala</Label>
                        <select class="form-select" bind:value={integratedDisplay}>
                            <option value={false}>Modo teatro</option>
                            <option value={true}>Modo piscina</option>
                        </select>
                    </FormGroup>

                    <FormGroup>
                        <Label>Tiempo entre mensajes</Label>
                        <select class="form-select" bind:value={secondsBetweenMessages}>
                            <option value={10}>10 segundos</option>
                            <option value={20}>20 segundos</option>
                            <option value={30}>30 segundos</option>
                            <option value={40}>40 segundos</option>
                        </select>
                    </FormGroup>
                    
                    <!-- <FormCheck label="Pantalla integrada" bind:checked={integratedDisplay}></FormCheck>-->

                    <FormGroup>
                        <FormCheck bind:checked={showRoomQrInMonitor} label="Mostrar QR y PIN en el monitor" />
                    </FormGroup>

                    <FormGroup>
                        <Label>Premio Línea/Figura</Label>
                        <Input type="text" bind:value={awardFigureText} />
                    </FormGroup>

                    <FormGroup>
                        <Label>Premio Bingo</Label>
                        <Input type="text" bind:value={awardBingoText} />
                    </FormGroup>
        
                    <div class="f-between f-x-center">
                        <FormCheck label="Activado" bind:checked={enabled}></FormCheck>
                        <Button type="button" color="primary" on:click={onSubmit} disabled={isSaving}>
                            {#if isSaving}
                                <Icon name="cloud-upload" /> Guardando...
                            {:else}
                                <Icon name="cloud-upload" /> Guardar
                            {/if}
                        </Button>
                    </div>
                </Col>
            </Row>

        </FormFeedback>

    </TabPane>

    <TabPane tab="Mensajes" tabId="messages" class="pt-3">
        <MessagesManager roomId={room.id} />
    </TabPane>
    
    <TabPane tab="Imágenes" tabId="pictures" class="pt-3">

        <InputFiles
            bind:this={inputFiles}
            accept="image/jpeg,image/png"
            on:files={onPictures} />

        <div class="gallery">

            <button type="button" class="upload-picture-btn" on:click={() => inputFiles.openDialog()}>
                <Icon name="plus" />
            </button>

            <Each state={room.gallery} let:item={pictureId}>
                <div class="picture">
                    <AspectRatio>
                        <img src={pictureUrlFactory.getPictureUrl(pictureId)} alt="">
                    </AspectRatio>
                    <button type="button" class="btn btn-danger btn-sm delete-picture-btn" on:click={() => onRemovePicture(pictureId)}>
                        Eliminar
                    </button>
                </div>
            </Each>
        </div>

    </TabPane>
    
    <slot />

</TabContent>

<style lang="scss">

    .gallery {
        display: grid;
        grid-template-columns: repeat(3, 15rem);
        gap: 0.5rem;
    }

    .picture {
        background-color: #222;
        position: relative;
        
        img {
            width: 100%;
            height: 100%;
            object-fit: contain;
        }

    }

    .upload-picture-btn {
        padding: 0;
        box-sizing: border-box;
        border: 1px dashed var(--color-primary);
        font-size: 4rem;
    }

    .delete-picture-btn {
        position: absolute;
        bottom: 0.5rem;
        left: 0.5rem;
        z-index: 2;
    }

</style>
